// KidSvgColoring — SVG path-tap coloring. The reliable architecture
// for a kids coloring app: every enclosed region is its own <path>,
// kid taps a path, we set its fill attribute to the active color.
// No flood-fill BFS, no pixel reads, no CORS-tainted canvas, no
// alpha-mask threshold tuning. Tap-to-fill works as long as the
// SVG has clean enclosed paths (Recraft Vectorize generally does).
//
// EMOJI-IN-UI EXCEPTION: kids-mode opt-in.

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import { readAiTokenDenial, KID_TOKENS_MESSAGE } from "../../lib/aiTokenError";
import { COLOR, FONT } from "../../lib/tokens";
import { playKidVoice } from "../../lib/kidVoice";

const PALETTE = [
  "#E84A5F", // red
  "#FF8A3D", // orange
  "#FFD23F", // yellow
  "#52B788", // green
  "#5DA9E9", // sky
  "#3D6BB8", // blue
  "#9B5DE5", // purple
  "#F4B6C2", // pink
  "#8B5E3C", // brown
  "#F2D5B7", // peach (skin tone)
  "#1A1A1A", // black
  "#FFFFFF", // white (eraser)
];

const UNDO_CAP = 16;

// Selector matching every paintable element. We deliberately exclude
// outline-only strokes (treating them as line art the kid can't
// recolor); Recraft's vectorize output typically uses fill="none"
// or stroke-only on outline paths. Coloring targets are paths/shapes
// with no fill set or a near-white fill.
const PAINTABLE_SEL = "path, rect, circle, ellipse, polygon, polyline";

export function KidSvgColoring({
  kidUserId,
  coloringPageId,
  lineArtSvgURL,
  sourceImageURL: initialSourceImageURL = null,
  coloredSvgState,    // optional — JSON map of path-key → color from save
  petName,
  comboName,
  onClose,
}) {
  const containerRef = useRef(null);
  const [svgHtml, setSvgHtml] = useState(null);
  const [color, setColor] = useState(PALETTE[0]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [savedToast, setSavedToast] = useState(false);
  // Map of stable path-key (index in paintable list) → applied color.
  // Drives both the rendered fills and the save serializer.
  //
  // colored_state has two historical shapes:
  //   - LEGACY (pre-magic-save): flat { "0": "#FF0000", "1": "#00FF00" }
  //   - CURRENT: { fills: {0,1,...}, magicMask: "data:image/png;…" }
  // Extract just the fills map so undo / apply-effects don't iterate
  // through the magicMask base64 blob. Backward-compat: when the row
  // came in legacy shape (no fills sub-object), use the whole object.
  const [fills, setFills] = useState(() => {
    const s = coloredSvgState;
    if (!s || typeof s !== "object") return {};
    if (s.fills && typeof s.fills === "object") return s.fills;
    // Legacy shape: treat the whole object as the fills map, but
    // strip the magicMask key if it happens to be present.
    const { magicMask: _drop, ...rest } = s;
    return rest;
  });
  const undoStackRef = useRef([]);

  // Magic Pen reveal mode. Off by default — the standard tap-to-fill
  // experience. Toggling on lazy-generates the full-color stylized
  // source for this page (one gpt-image-2 call per page, persisted
  // via generate-coloring-source edge fn) and stacks: color image →
  // white mask canvas → line-art SVG with mix-blend-mode:multiply.
  // The kid drags to erase the mask, revealing the color underneath.
  // The line art always stays visible because multiply turns the
  // SVG's white interior transparent while keeping black outlines.
  const [magicMode, setMagicMode] = useState(false);
  const [sourceImageURL, setSourceImageURL] = useState(initialSourceImageURL);
  const [magicGenerating, setMagicGenerating] = useState(false);
  const maskCanvasRef = useRef(null);
  const magicLayerRef = useRef(null);

  // Fetch + sanitize the SVG markup. Strip width/height attrs so it
  // scales via CSS. Add a viewBox if missing.
  useEffect(() => {
    if (!lineArtSvgURL) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(lineArtSvgURL);
        if (!res.ok) throw new Error(`fetch ${res.status}`);
        let text = await res.text();
        // Remove leading XML decl + comments + DOCTYPE for clean inline render.
        text = text.replace(/<\?xml[^?]*\?>/g, "").replace(/<!--[\s\S]*?-->/g, "").trim();
        if (cancelled) return;
        setSvgHtml(text);
      } catch (e) {
        console.error("[svg-coloring] fetch failed:", e);
        setError("Couldn't load the picture");
      }
    })();
    return () => { cancelled = true; };
  }, [lineArtSvgURL]);

  // Index paintable paths after mount + apply any pre-saved fills.
  useEffect(() => {
    const root = containerRef.current;
    if (!root || !svgHtml) return;
    const svg = root.querySelector("svg");
    if (!svg) return;
    // Force-scale to fit the container.
    svg.removeAttribute("width");
    svg.removeAttribute("height");
    svg.setAttribute("style", "width:100%;height:100%;display:block;");
    if (!svg.getAttribute("viewBox")) {
      svg.setAttribute("viewBox", "0 0 1024 1024");
    }
    // Tag every paintable region with a stable data-key so save
    // serializes consistently across re-renders.
    const paintables = svg.querySelectorAll(PAINTABLE_SEL);
    paintables.forEach((el, i) => {
      el.setAttribute("data-key", String(i));
      // Mark outline-only paths so we don't accept fill-taps on them.
      const fillAttr = (el.getAttribute("fill") || "").toLowerCase();
      const isOutline = fillAttr === "none" ||
        (!fillAttr && (el.getAttribute("stroke") || "").length > 0 && !el.querySelector("[fill]"));
      if (isOutline) el.setAttribute("data-outline", "true");
      // Apply pre-saved fill if any.
      const saved = fills[String(i)];
      if (saved) el.setAttribute("fill", saved);
      el.style.cursor = isOutline ? "default" : "pointer";
    });
  }, [svgHtml, fills]);

  // Tap delegation. One listener on the container; figure out which
  // path was hit via event.target.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const onTap = (e) => {
      const t = e.target;
      if (!t || t === root) return;
      // Walk up to find the nearest paintable element with data-key
      // (event.target on a nested SVG might be a child shape).
      let cur = t;
      while (cur && cur !== root) {
        if (cur.matches && cur.matches(PAINTABLE_SEL) && cur.hasAttribute("data-key")) break;
        cur = cur.parentNode;
      }
      if (!cur || cur === root) return;
      if (cur.getAttribute("data-outline") === "true") return;
      const key = cur.getAttribute("data-key");
      if (!key) return;
      undoStackRef.current.push({ key, prev: fills[key] || null });
      if (undoStackRef.current.length > UNDO_CAP) undoStackRef.current.shift();
      setFills((prev) => ({ ...prev, [key]: color }));
      e.preventDefault();
    };
    root.addEventListener("pointerdown", onTap, { passive: false });
    return () => { root.removeEventListener("pointerdown", onTap); };
  }, [fills, color]);

  const undo = () => {
    const last = undoStackRef.current.pop();
    if (!last) return;
    setFills((prev) => {
      const next = { ...prev };
      if (last.prev === null) delete next[last.key];
      else next[last.key] = last.prev;
      return next;
    });
  };

  // Magic Pen activation — three-stage flow so the kid is never
  // blocked while we wait on ~$0.20 of gpt-image-2 time:
  //
  //   1. First tap (no source yet) → kick off generation in the
  //      background, button flips to "MAGIC…" state. Kid keeps
  //      coloring the line art normally; nothing on screen is
  //      locked.
  //   2. Generation completes → sourceImageURL fills in,
  //      magicGenerating drops to false, button flips to "READY"
  //      state. We do NOT auto-enter magic mode — the kid stays in
  //      normal coloring so it doesn't yank them mid-stroke.
  //   3. Second tap (source cached) → setMagicMode(true), reveal
  //      layers mount, kid drags to reveal color.
  //
  // Cost-controlled: the edge fn short-circuits when
  // source_image_url is already set on the row, so a page only
  // pays the generation cost once. Errors keep the kid in normal
  // mode (no half-broken Magic state).
  const activateMagic = useCallback(() => {
    setError(null);
    // Voice announcement so the kid hears "Magic Color!" whether
    // we're entering magic mode immediately (cached source) or
    // kicking off the background generation.
    playKidVoice("magic-color");
    if (sourceImageURL) { setMagicMode(true); return; }
    if (!coloringPageId || !kidUserId) {
      setError("Magic needs a saved page first.");
      return;
    }
    if (magicGenerating) return; // already in flight
    setMagicGenerating(true);
    void (async () => {
      try {
        const { data, error: e } = await supabase.functions.invoke(
          "generate-coloring-source",
          { body: { kidUserId, pageId: coloringPageId } },
        );
        if (e) throw e;
        const url = data?.sourceImageURL;
        if (!url) throw new Error("no sourceImageURL in response");
        setSourceImageURL(url);
        // Don't auto-flip to magic mode — the kid tapped while
        // they were coloring. Let the button flip to READY and
        // wait for an explicit second tap.
      } catch (e) {
        console.error("[magic-pen] activate failed:", e);
        // Weekly AI-token allowance exhausted → honest kid-friendly
        // copy; anything else stays the generic retry line.
        const denial = await readAiTokenDenial(e, null);
        setError(denial ? KID_TOKENS_MESSAGE : "Magic broke — try again in a sec");
      } finally {
        setMagicGenerating(false);
      }
    })();
  }, [sourceImageURL, kidUserId, coloringPageId, magicGenerating]);

  // Mask canvas — init ONCE per sourceImageURL (sizing + white
  // fill), separate from the pointer-listener attachment. The
  // earlier single-effect re-ran on every magicMode toggle, which
  // white-filled the canvas and erased the kid's reveal every time
  // they tapped DONE → MAGIC. Now: white-fill happens only when a
  // new source loads; the mask persists across mode toggles.
  const maskInitedForRef = useRef(null);
  useEffect(() => {
    if (!sourceImageURL) return;
    if (maskInitedForRef.current === sourceImageURL) return; // already inited
    const layer  = magicLayerRef.current;
    const canvas = maskCanvasRef.current;
    if (!layer || !canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = layer.getBoundingClientRect();
    // Guard against zero-size init when the layer is display:none
    // on first mount (magicMode false). Retry on next layout via
    // requestAnimationFrame so the mask gets a real size.
    if (rect.width < 2 || rect.height < 2) {
      const raf = requestAnimationFrame(() => {
        maskInitedForRef.current = null; // force re-run
      });
      return () => cancelAnimationFrame(raf);
    }
    canvas.width  = Math.max(1, Math.round(rect.width  * dpr));
    canvas.height = Math.max(1, Math.round(rect.height * dpr));
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    // Restore prior magic reveal when the saved row carries one.
    // Falls back to opaque-white fill (the fresh-mask default) when
    // no saved state exists OR the stored data URL is malformed.
    const savedMask = coloredSvgState?.magicMask;
    if (savedMask && typeof savedMask === "string" && savedMask.startsWith("data:image/")) {
      const img = new Image();
      img.onload = () => {
        try {
          ctx.clearRect(0, 0, rect.width, rect.height);
          ctx.drawImage(img, 0, 0, rect.width, rect.height);
        } catch (err) {
          console.warn("[svg-coloring] mask restore drawImage failed:", err?.message || err);
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, rect.width, rect.height);
        }
      };
      img.onerror = () => {
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, rect.width, rect.height);
      };
      img.src = savedMask;
    } else {
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, rect.width, rect.height);
    }
    maskInitedForRef.current = sourceImageURL;
  }, [sourceImageURL, magicMode, coloredSvgState]);

  // Pointer listeners on the mask. Attached only while magic mode
  // is active so taps in normal mode don't erase the mask.
  useEffect(() => {
    if (!magicMode) return undefined;
    const layer  = magicLayerRef.current;
    const canvas = maskCanvasRef.current;
    if (!layer || !canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let drawing = false;
    let lastX = 0, lastY = 0;

    const localCoords = (e) => {
      const r = layer.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };

    const onDown = (e) => {
      drawing = true;
      const { x, y } = localCoords(e);
      lastX = x; lastY = y;
      // Tap-only stroke: paint a single dot so a fingertip tap
      // reveals a spot too.
      ctx.globalCompositeOperation = "destination-out";
      ctx.beginPath();
      ctx.arc(x, y, 30, 0, Math.PI * 2);
      ctx.fill();
      e.preventDefault();
    };
    const onMove = (e) => {
      if (!drawing) return;
      const { x, y } = localCoords(e);
      ctx.globalCompositeOperation = "destination-out";
      ctx.lineWidth = 60;
      ctx.strokeStyle = "rgba(0,0,0,1)";
      ctx.beginPath();
      ctx.moveTo(lastX, lastY);
      ctx.lineTo(x, y);
      ctx.stroke();
      lastX = x; lastY = y;
      e.preventDefault();
    };
    const onUp = () => { drawing = false; };

    canvas.addEventListener("pointerdown",  onDown, { passive: false });
    canvas.addEventListener("pointermove",  onMove, { passive: false });
    canvas.addEventListener("pointerup",    onUp);
    canvas.addEventListener("pointercancel", onUp);
    canvas.addEventListener("pointerleave",  onUp);
    return () => {
      canvas.removeEventListener("pointerdown",  onDown);
      canvas.removeEventListener("pointermove",  onMove);
      canvas.removeEventListener("pointerup",    onUp);
      canvas.removeEventListener("pointercancel", onUp);
      canvas.removeEventListener("pointerleave",  onUp);
    };
  }, [magicMode, sourceImageURL]);

  const resetMask = () => {
    const layer  = magicLayerRef.current;
    const canvas = maskCanvasRef.current;
    if (!layer || !canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const rect = layer.getBoundingClientRect();
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, rect.width, rect.height);
  };

  // Save: serialize the current SVG (with applied fills baked in via
  // attribute setters), upload to Storage, patch the row. Also
  // composite to PNG for the gallery thumbnail.
  //
  // Two failure modes the button guard below now covers: (a) magic
  // mode unmounts the inline-SVG container so containerRef.current
  // is null; (b) the SVG fetch hasn't resolved so svgHtml is null
  // and the dangerouslySetInnerHTML target is empty. Both produced
  // "svg not mounted" in the wild before the disabled-state landed.
  const save = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const root = containerRef.current;
      const svg = root?.querySelector("svg");
      if (!svg) {
        setError("Picture still loading — try again in a sec");
        return;
      }
      // Bake applied fills into the SVG markup string.
      const serialized = new XMLSerializer().serializeToString(svg);
      const svgBlob = new Blob([
        '<?xml version="1.0" encoding="UTF-8"?>\n', serialized,
      ], { type: "image/svg+xml" });
      const svgPath = `colored/${crypto.randomUUID()}.svg`;
      const { error: upErr } = await supabase.storage
        .from("kid-coloring-pages")
        .upload(svgPath, svgBlob, {
          contentType:  "image/svg+xml",
          upsert:       false,
          cacheControl: "public, max-age=2592000, immutable",
        });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("kid-coloring-pages").getPublicUrl(svgPath);
      const coloredURL = pub.publicUrl;

      // Rasterize for the gallery thumbnail. Two paths:
      //   - Magic mode: composite (color image + mask + line art)
      //     so the saved thumbnail shows the kid's actual reveal
      //     work, not a blank line-art frame.
      //   - Normal mode: render the SVG with current fills baked in.
      //
      // Also persist the mask canvas state as a base64 PNG into
      // colored_state.magicMask so reopening in magic mode restores
      // their exact reveal-in-progress instead of starting from a
      // fresh white opaque cover.
      try {
        const out = document.createElement("canvas");
        out.width = 512;
        out.height = 512;
        const octx = out.getContext("2d");
        octx.fillStyle = "#FFFFFF";
        octx.fillRect(0, 0, 512, 512);

        let magicMaskBase64 = null;

        if (magicMode && sourceImageURL && maskCanvasRef.current) {
          // Magic composite path. Draw color image, then mask
          // (white-where-opaque + transparent-where-erased), then
          // line art with multiply blend so black outlines stay and
          // white interior lets the color through underneath.
          const colorImg = new Image();
          colorImg.crossOrigin = "anonymous";
          await new Promise((res, rej) => {
            colorImg.onload = res;
            colorImg.onerror = rej;
            colorImg.src = sourceImageURL;
          }).catch(() => {});
          if (colorImg.complete && colorImg.naturalWidth > 0) {
            octx.drawImage(colorImg, 0, 0, 512, 512);
          }
          octx.drawImage(maskCanvasRef.current, 0, 0, 512, 512);

          // Line art over top with multiply blend. lineArtSvgURL
          // is our own bucket URL so cross-origin is fine.
          const lineImg = new Image();
          lineImg.crossOrigin = "anonymous";
          await new Promise((res, rej) => {
            lineImg.onload = res;
            lineImg.onerror = rej;
            lineImg.src = lineArtSvgURL;
          }).catch(() => {});
          if (lineImg.complete && lineImg.naturalWidth > 0) {
            octx.globalCompositeOperation = "multiply";
            octx.drawImage(lineImg, 0, 0, 512, 512);
            octx.globalCompositeOperation = "source-over";
          }

          // Serialize the mask itself so reopen can restore the
          // partial reveal. ~50-200KB base64 per mask depending on
          // erasure complexity; fits comfortably in a JSONB cell.
          try {
            magicMaskBase64 = maskCanvasRef.current.toDataURL("image/png");
          } catch (e) {
            console.warn("[svg-coloring] mask toDataURL failed:", e?.message || e);
          }
        } else {
          // Standard SVG-only thumbnail (no magic active).
          const svgImg = new Image();
          const dataUrl = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(serialized);
          await new Promise((res, rej) => {
            svgImg.onload = res;
            svgImg.onerror = rej;
            svgImg.src = dataUrl;
          });
          octx.drawImage(svgImg, 0, 0, 512, 512);
        }

        // JPEG, not PNG — egress discipline. This 512px thumbnail is
        // served back to every gallery viewer from a public bucket;
        // the white base fill above means no alpha is lost, and 0.85
        // keeps line art crisp at tile size.
        const pngBlob = await new Promise((res) => out.toBlob(res, "image/jpeg", 0.85));
        if (pngBlob) {
          const pngPath = `colored/${crypto.randomUUID()}.jpg`;
          await supabase.storage.from("kid-coloring-pages").upload(pngPath, pngBlob, {
            contentType: "image/jpeg", upsert: false,
            cacheControl: "public, max-age=2592000, immutable",
          });
          const { data: pngPub } = supabase.storage.from("kid-coloring-pages").getPublicUrl(pngPath);
          if (coloringPageId) {
            // Persist editable state: SVG fills map + (when magic
            // active) the mask base64 PNG. Reopen reads both: tap-
            // fills are re-applied to SVG paths; mask is drawn back
            // onto the canvas BEFORE pointer listeners attach.
            const stateBlob = { fills, ...(magicMaskBase64 ? { magicMask: magicMaskBase64 } : {}) };
            await supabase.from("kid_coloring_pages")
              .update({
                colored_url:   pngPub.publicUrl,
                colored_state: stateBlob,
                colored_at:    new Date().toISOString(),
              })
              .eq("id", coloringPageId)
              .eq("kid_user_id", kidUserId);
          }
        }
      } catch (rastErr) {
        // Non-fatal — SVG is saved; thumbnail can be regenerated later.
        console.warn("[svg-coloring] thumbnail rasterize failed:", rastErr);
        if (coloringPageId) {
          // Same shape as the happy path: { fills, magicMask? }. Keeps
          // the load-side extractor's contract consistent so reopen
          // never has to handle a fills-flat fallback.
          await supabase.from("kid_coloring_pages")
            .update({
              colored_url:   coloredURL,
              colored_state: { fills },
              colored_at:    new Date().toISOString(),
            })
            .eq("id", coloringPageId)
            .eq("kid_user_id", kidUserId);
        }
      }

      setSavedToast(true);
      setTimeout(() => {
        setSavedToast(false);
        if (typeof onClose === "function") onClose();
      }, 1400);
    } catch (e) {
      console.error("[svg-coloring] save failed:", e);
      setError(e?.message || "Couldn't save");
    } finally {
      setBusy(false);
    }
  }, [busy, coloringPageId, kidUserId, onClose, fills, magicMode, sourceImageURL, lineArtSvgURL]);

  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "#FAF6EE",
      zIndex: 320,
      display: "flex", flexDirection: "column",
      paddingTop: "env(safe-area-inset-top)",
      paddingBottom: "env(safe-area-inset-bottom)",
    }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 16px", borderBottom: `1px solid ${COLOR.border}`,
      }}>
        <button type="button" onClick={onClose}
          style={{ background: "transparent", border: "none", fontSize: 28, cursor: "pointer", padding: 4 }}
          aria-label="Close">✕</button>
        <div style={{
          fontFamily: FONT.itemTitle, fontStyle: "italic",
          fontSize: 18, color: COLOR.ink, textAlign: "center", flex: 1,
        }}>
          {petName && comboName ? `${petName} & ${comboName}` : (comboName || "Color it!")}
        </div>
        {(() => {
          // Disable SAVE only while the SVG fetch is still in
          // flight (svgHtml null = empty dangerouslySetInnerHTML =
          // no SVG to serialize). The SVG container stays mounted
          // when magic mode is active (display:none) so the save
          // path works in both modes.
          const canSave = !!svgHtml;
          const disabled = busy || !canSave;
          return (
            <button type="button" onClick={save} disabled={disabled}
              className="mcm-focusable"
              style={{
                padding: "8px 14px", background: COLOR.leaf, color: "#fff",
                border: "none", borderRadius: 10, fontFamily: FONT.mono,
                fontSize: 12, fontWeight: 700, letterSpacing: "0.08em",
                cursor: busy ? "wait" : (disabled ? "not-allowed" : "pointer"),
                opacity: disabled ? 0.5 : 1,
                touchAction: "manipulation",
              }}>
              {busy ? "…" : "SAVE"}
            </button>
          );
        })()}
      </div>

      {/* Canvas area — two presentations stacked at the same slot.
          NORMAL: inline SVG with path-tap fills.
          MAGIC : color image → white mask canvas → line-art image with
                  mix-blend-mode:multiply so the white interior turns
                  transparent and the lines composite cleanly over the
                  revealed color. The SVG line art is rendered as <img>
                  (not inline) so the dangerously-set inline SVG stays
                  unmounted while in magic mode and we avoid a double
                  pointerdown listener race. */}
      <div style={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden", padding: 8,
      }}>
        <div style={{
          position: "relative",
          width: "min(90vw, calc(100vh - 200px))",
          aspectRatio: "1 / 1",
          background: "#FFFFFF",
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: `0 4px 16px ${COLOR.shadowSoft || "rgba(0,0,0,0.08)"}`,
          touchAction: "none",
        }}>
          {/* NORMAL mode: tap-to-fill on the inline SVG.
              Kept MOUNTED even while magic mode is active (display:
              none) so the save path always has access to the SVG
              element with the kid's tap-fills baked into its
              attributes. The earlier conditional-mount caused
              "svg not mounted" errors AND blocked saving while in
              magic mode. */}
          <div
            ref={containerRef}
            style={{
              position: "absolute",
              inset: 0,
              display: magicMode ? "none" : "block",
            }}
            // dangerouslySetInnerHTML is the standard pattern for inline
            // SVG from a fetched source. The SVG comes from our own
            // edge fn → Recraft (no user input), so XSS isn't a concern.
            dangerouslySetInnerHTML={{ __html: svgHtml || "" }}
          />

          {/* MAGIC mode: stacked color / mask / line-art layers.
              Stays MOUNTED whenever sourceImageURL exists (display:
              none when magicMode is off) so the mask canvas pixels
              persist across DONE/re-enter — the kid's reveal isn't
              destroyed when they pop back to normal mode and
              return. */}
          {sourceImageURL && (
            <div ref={magicLayerRef} style={{
              position: "absolute",
              inset: 0,
              display: magicMode ? "block" : "none",
            }}>
              <img
                src={sourceImageURL}
                alt=""
                draggable={false}
                style={{
                  position: "absolute", inset: 0,
                  width: "100%", height: "100%",
                  objectFit: "contain",
                  background: "#FFFFFF",
                  pointerEvents: "none",
                  userSelect: "none",
                }}
              />
              <canvas
                ref={maskCanvasRef}
                style={{
                  position: "absolute", inset: 0,
                  width: "100%", height: "100%",
                  touchAction: "none",
                  // Sits above the color image, below the line art.
                  zIndex: 2,
                }}
              />
              <img
                src={lineArtSvgURL}
                alt=""
                draggable={false}
                style={{
                  position: "absolute", inset: 0,
                  width: "100%", height: "100%",
                  objectFit: "contain",
                  pointerEvents: "none",
                  userSelect: "none",
                  // Multiply turns the SVG's white interior transparent
                  // against whatever pixel is underneath (the mask or
                  // the color image), while preserving black outlines.
                  mixBlendMode: "multiply",
                  zIndex: 3,
                }}
              />
            </div>
          )}

        </div>
      </div>

      {/* Lockscreen retired for the magic-generating case — the
          kid stays in normal coloring while gpt-image-2 runs. The
          MAGIC button reflects the state (MAGIC… → READY ✨ →
          DONE) so they know it's working without being blocked. */}

      {/* Saved toast */}
      {savedToast && (
        <div style={{
          position: "fixed", left: "50%", top: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 400,
          padding: "20px 28px",
          background: COLOR.leaf, color: "#fff",
          borderRadius: 20,
          fontFamily: FONT.itemTitle, fontStyle: "italic",
          fontSize: 22, textAlign: "center",
          boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
          pointerEvents: "none",
          animation: "kid-saved-pop 320ms cubic-bezier(0.34, 1.56, 0.64, 1)",
        }}>
          ✓ Saved to your coloring book!
          <style>{`@keyframes kid-saved-pop {
            from { transform: translate(-50%, -50%) scale(0.6); opacity: 0; }
            to   { transform: translate(-50%, -50%) scale(1);   opacity: 1; }
          }`}</style>
        </div>
      )}

      {/* Palette + undo */}
      <div style={{ padding: "8px 12px 12px" }}>
        {error && (
          <div style={{
            marginBottom: 8, padding: "8px 12px",
            background: COLOR.roseDeep, border: `1px solid ${COLOR.roseDim}`,
            borderRadius: 8, color: COLOR.rose,
            fontFamily: FONT.forma, fontSize: 12,
          }}>
            {error}
          </div>
        )}
        {/* Palette — hidden in magic mode (the kid is revealing, not
            picking colors). Re-renders when toggled back. */}
        {!magicMode && (
          <div style={{
            display: "flex", gap: 6, marginBottom: 10,
            overflowX: "auto", paddingBottom: 4,
          }}>
            {PALETTE.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                aria-label={`color ${c}`}
                style={{
                  flex: "0 0 auto",
                  width: 44, height: 44,
                  borderRadius: 22,
                  background: c,
                  border: color === c ? `3px solid ${COLOR.ink}` : `2px solid ${COLOR.border}`,
                  cursor: "pointer",
                  touchAction: "manipulation",
                }}
              />
            ))}
          </div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          {/* UNDO — color-section mode only. Magic mode doesn't have
              per-stroke undo (the mask is the kid's exploration; the
              RESET button below clears the whole mask instead). */}
          {!magicMode && (
            <button
              type="button"
              onClick={undo}
              className="mcm-focusable"
              style={{
                flex: 1, padding: "10px 4px",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                background: "#fff", border: `2px solid ${COLOR.border}`,
                borderRadius: 10, cursor: "pointer", touchAction: "manipulation",
              }}>
              <span style={{ fontSize: 22 }}>↶</span>
              <span style={{
                fontFamily: FONT.mono, fontSize: 10, fontWeight: 700,
                color: COLOR.ink, letterSpacing: "0.06em",
              }}>UNDO</span>
            </button>
          )}
          {/* MAGIC mode: a RESET button that re-opaques the mask so
              the kid can start the reveal over without leaving the mode. */}
          {magicMode && (
            <button
              type="button"
              onClick={resetMask}
              className="mcm-focusable"
              style={{
                flex: 1, padding: "10px 4px",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                background: "#fff", border: `2px solid ${COLOR.border}`,
                borderRadius: 10, cursor: "pointer", touchAction: "manipulation",
              }}>
              <span style={{ fontSize: 22 }}>↺</span>
              <span style={{
                fontFamily: FONT.mono, fontSize: 10, fontWeight: 700,
                color: COLOR.ink, letterSpacing: "0.06em",
              }}>RESET</span>
            </button>
          )}
          {/* Magic Pen toggle — four visual states:
                 MAGIC      (idle, no source yet)       → tap kicks off generation
                 MAGIC…     (generating, ~10-15s)       → still tappable to reveal
                                                          progress, but generation
                                                          runs in the background so
                                                          the kid keeps coloring
                 READY ✨    (source ready, not active)  → tap enters magic mode
                 DONE       (magicMode active)          → tap returns to normal */}
          {(() => {
            const ready = !!sourceImageURL && !magicMode;
            const generating = magicGenerating && !sourceImageURL;
            const label = magicMode ? "DONE"
              : generating ? "MAGIC…"
              : ready ? "READY"
              : "MAGIC";
            const onClick = magicMode
              ? () => setMagicMode(false)
              : (ready
                  ? () => setMagicMode(true)
                  : activateMagic);
            return (
              <button
                type="button"
                onClick={onClick}
                className="mcm-focusable"
                aria-label={magicMode ? "back to coloring" : (ready ? "reveal magic" : "magic pen")}
                style={{
                  flex: 1, padding: "10px 4px",
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                  background: magicMode ? "#9B5DE5" : (ready ? "rgba(155,93,229,0.16)" : "#fff"),
                  color: magicMode ? "#fff" : COLOR.ink,
                  border: `2px solid ${magicMode ? "#7A3FCF" : "#9B5DE5"}`,
                  borderRadius: 10,
                  cursor: "pointer",
                  touchAction: "manipulation",
                  position: "relative",
                  overflow: "hidden",
                }}>
                {/* Subtle pulse while generating — non-blocking visual */}
                {generating && (
                  <>
                    <style>{`
                      @keyframes magicShimmer {
                        0%   { background-position: -200% 0; }
                        100% { background-position:  200% 0; }
                      }
                    `}</style>
                    <div style={{
                      position: "absolute",
                      inset: 0,
                      background: "linear-gradient(115deg, transparent 30%, rgba(155,93,229,0.22) 50%, transparent 70%)",
                      backgroundSize: "200% 100%",
                      animation: "magicShimmer 1400ms ease-in-out infinite",
                      pointerEvents: "none",
                    }} />
                  </>
                )}
                <span style={{ fontSize: 22, position: "relative" }}>✨</span>
                <span style={{
                  fontFamily: FONT.mono, fontSize: 10, fontWeight: 700,
                  color: magicMode ? "#fff" : "#7A3FCF",
                  letterSpacing: "0.06em",
                  position: "relative",
                }}>
                  {label}
                </span>
              </button>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
